import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV = [
  { label: "Story", to: "/#story", type: "hash" },
  { label: "Gallery", to: "/#gallery", type: "hash" },
  { label: "Visit", to: "/#visit", type: "hash" },
  { label: "My Booking", to: "/lookup", type: "route" },
];

export default function SiteHeader() {
  const [open, setOpen] = useState(false);

  return (
    <header className="fixed top-0 inset-x-0 z-50 glass-header">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 md:px-10 py-3 md:py-4">
        <Link to="/" className="flex items-center gap-3 min-w-0" data-testid="header-logo">
          <span className="font-display text-lg sm:text-xl md:text-2xl tracking-tight text-[#1A2B3C] truncate">
            Caldera View Bar Restaurant
          </span>
          <span className="hidden lg:inline-block text-[10px] tracking-[0.28em] uppercase text-[#8C7A6B] shrink-0">
            Santorini
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-10 text-sm text-[#1A2B3C]">
          {NAV.map((item) =>
            item.type === "route" ? (
              <Link
                key={item.label}
                to={item.to}
                className="hover:text-[#D35400] transition-colors"
                data-testid={`nav-${item.label.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {item.label}
              </Link>
            ) : (
              <a
                key={item.label}
                href={item.to}
                className="hover:text-[#D35400] transition-colors"
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                {item.label}
              </a>
            )
          )}
        </nav>

        {/* Desktop CTA */}
        <Link to="/book" className="hidden sm:block" data-testid="header-book-cta">
          <Button className="btn-terracotta rounded-full px-6 h-10">Reserve a Table</Button>
        </Link>

        {/* Mobile menu button */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden text-[#1A2B3C] h-10 w-10 shrink-0"
              data-testid="mobile-menu-trigger"
              aria-label="Open menu"
            >
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="bg-[#F9F6F0] border-l border-[#E5E0D8] w-[86vw] max-w-sm" data-testid="mobile-menu">
            <div className="flex items-center justify-between mb-10">
              <span className="font-display text-xl text-[#1A2B3C]">Menu</span>
              <button
                onClick={() => setOpen(false)}
                className="text-[#8C7A6B] hover:text-[#D35400]"
                aria-label="Close menu"
                data-testid="mobile-menu-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-col gap-1">
              {NAV.map((item) => {
                const testid = `mobile-nav-${item.label.replace(/\s+/g, "-").toLowerCase()}`;
                if (item.type === "route") {
                  return (
                    <Link
                      key={item.label}
                      to={item.to}
                      onClick={() => setOpen(false)}
                      className="font-display text-2xl text-[#1A2B3C] hover:text-[#D35400] py-3 border-b border-[#E5E0D8]"
                      data-testid={testid}
                    >
                      {item.label}
                    </Link>
                  );
                }
                return (
                  <a
                    key={item.label}
                    href={item.to}
                    onClick={() => setOpen(false)}
                    className="font-display text-2xl text-[#1A2B3C] hover:text-[#D35400] py-3 border-b border-[#E5E0D8]"
                    data-testid={testid}
                  >
                    {item.label}
                  </a>
                );
              })}
            </nav>
            <Link
              to="/book"
              onClick={() => setOpen(false)}
              className="mt-8 block"
              data-testid="mobile-book-cta"
            >
              <Button className="btn-terracotta w-full rounded-full h-12 text-base">
                Reserve a Table
              </Button>
            </Link>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
